import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
} from '@angular/core';

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';

import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  HttpClientXsrfModule,
} from '@angular/common/http';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule } from '@ngrx/router-store';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastrModule } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { NgxPaginationModule } from 'ngx-pagination';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerModule } from 'ngx-spinner';

import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { NgChartsModule } from 'ng2-charts';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

import {
  OAuthLogger,
  OAuthModule,
  provideOAuthClient,
  UrlHelperService,
} from 'angular-oauth2-oidc';

import { environment } from '../environments/environment';
import { MaterialModule } from 'src/material-module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';

import { AppReducers } from './store/reducers/app.reducer';

import { HomeComponent } from './home/home.component';
import { SurveyUrlComponent } from './survey-url/survey-url.component';

import { PerformanceMeasuresComponent } from './performance-measures/performance-measures.component';
import { OdpPerformanceMeasuresComponent } from './performance-measures/odp-performance-measures/odp-performance-measures.component';
import { OdpResolutionPerformanceMeasuresComponent } from './performance-measures/odp-resolution-performance-measures/odp-resolution-performance-measures.component';

import { RegisterComponent } from './register/register.component';
import { LoginComponent } from './login/login.component';
import { MainComponent } from './main/main.component';

import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

import { EntitySearchComponent } from './entity-search/entity-search.component';
import { AdvancedFiltersComponent } from './entity-search/advanced-filters/advanced-filters.component';

import { BusinessProcessComponent } from './business-process/business-process.component';

import { UserManagementComponent } from './user-management/user-management.component';
import { ManageGroupUsersComponent } from './manage-group-users/manage-group-users.component';

import { IncidentComponent } from './incident/incident.component';
import { SupervisorMappingComponent } from './supervisor-mapping/supervisor-mapping.component';
import { ConfirmPasswordComponent } from './confirm-password/confirm-password.component';

import { IncidentHistoryDialogComponent } from './business-process/incident-history-dialog/incident-history-dialog.component';

import { MapUserGroupComponent } from './map-user-group/map-user-group.component';
import { GroupMappingDialogComponent } from './login/group-mapping-dialog/group-mapping-dialog.component';

import { DashboardReportsComponent } from './dashboard-reports/dashboard-reports.component';
import { DashboardReportsModule } from './dashboard-reports/dashboard-reports.module';

import { LandingPageComponent } from './landingpage/landingpagecomponent';
import { RsLandingComponent } from './rs-landing/rs-landing.component';
import { DemoLandingComponent } from './demo-landing-page/demo-landing.component';

import { ResourceUploadComponent } from './resource-upload/resource-upload.component';
import { IncidentDisputeComponent } from './incident-dispute/incident-dispute.component';

import { RsHelpComponent } from './shared/components/rs-help/rs-help.component';
import { RSContactComponent } from './shared/components/rs-contact/rs-contact.component';

import { FilterCategoryPipe } from './shared/pipe/filter-category.pipe';
import { QuestionConsentFilterPipe } from './shared/pipe/filter-QuestionByConsent.pipe';

import { NotificationService } from './shared/services/notifications.service';
import { GlobalErrorHandler } from './shared/services/globalerrorhandler.service';
import { LoaderInterceptorService } from './shared/services/LoaderInterceptorService';
import { LoaderService } from './shared/services/loader.service';

import { SurveyUrlService } from 'src/app/survey-url/survey-url.service';

import { TokenInterceptorServices } from './auth/token.interceptor.service';

import { AUTH_SERVICE } from './auth/auth.token';
import { authServiceFactory } from './auth/auth-service.factory';
import { CognitoAuthService } from './shared/services/cognito-auth.service';
import { KeycloakAuthService } from './shared/services/keycloak-auth.service';

import { SiteminderAuthService } from './auth/oidc-config.service';
import { SiteminderLoginComponent } from './siteminder-login/siteminder-login.component';
import { OdpLoginComponent } from './odp-login/odp-login.component';

function logInitializationError(
  context: string,
  error: unknown
): void {
  try {
    const messages: string[] = [];
    let currentError: unknown = error;
    let depth = 0;

    while (currentError && depth < 5) {
      if (currentError instanceof Error) {
        messages.push(
          currentError.message || currentError.name
        );

        const nestedError = currentError as Error & {
          cause?: unknown;
          innerException?: unknown;
          innerError?: unknown;
        };

        currentError =
          nestedError.cause ??
          nestedError.innerException ??
          nestedError.innerError ??
          null;
      } else {
        messages.push('Unknown error');
        currentError = null;
      }

      depth++;
    }

    console.error(
      `${context}${
        messages.length
          ? `: ${messages.join(' | ')}`
          : ''
      }`
    );
  } catch {
    console.error(context);
  }
}

export function authInit(
  oidcConfigService: SiteminderAuthService
): () => Promise<void> {
  return async () => {
    try {
      await oidcConfigService.initializeOAuthService();
    } catch (error) {
      logInitializationError(
        'SiteMinder initialization failed',
        error
      );

      throw error;
    }
  };
}

@NgModule({
  declarations: [
    IncidentComponent,
    AppComponent,
    RegisterComponent,
    LoginComponent,
    MainComponent,
    HomeComponent,
    EntitySearchComponent,
    SurveyUrlComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    AdvancedFiltersComponent,
    BusinessProcessComponent,
    PerformanceMeasuresComponent,
    OdpPerformanceMeasuresComponent,
    OdpResolutionPerformanceMeasuresComponent,
    UserManagementComponent,
    ManageGroupUsersComponent,
    SupervisorMappingComponent,
    ConfirmPasswordComponent,
    DashboardReportsComponent,
    IncidentHistoryDialogComponent,
    MapUserGroupComponent,
    GroupMappingDialogComponent,
    LandingPageComponent,
    RsLandingComponent,
    DemoLandingComponent,
    ResourceUploadComponent,
    IncidentDisputeComponent,
    RsHelpComponent,
    RSContactComponent,

    // SiteMinder login remains NgModule-declared.
    SiteminderLoginComponent,
  ],

  imports: [
    NgbModule,
    MatChipsModule,
    NgxPaginationModule,
    HttpClientModule,
    NgxSpinnerModule,
    RouterModule,

    StoreModule.forRoot(AppReducers),

    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
    }),

    !environment.production
      ? StoreDevtoolsModule.instrument()
      : [],

    FontAwesomeModule,
    SharedModule,

    FilterCategoryPipe,
    QuestionConsentFilterPipe,

    AppRoutingModule,

    ToastrModule.forRoot(),

    FormsModule,
    ScrollingModule,
    CommonModule,
    NgChartsModule,
    ReactiveFormsModule,
    MaterialModule,

    HttpClientXsrfModule.withOptions({
      cookieName: 'XSRF-Token',
      headerName: 'X-XSRF-Token',
    }),

    BrowserAnimationsModule,
    BrowserModule,
    MatIconModule,

    NgMultiSelectDropDownModule.forRoot(),

    DashboardReportsModule,

    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.baseUrl],
        sendAccessToken: true,
      },
    }),

    // OdpLoginComponent is standalone.
    OdpLoginComponent,
  ],

  providers: [
    FilterCategoryPipe,
    QuestionConsentFilterPipe,

    NotificationService,
    LoaderService,
    CookieService,
    SurveyUrlService,

    {
      provide: AUTH_SERVICE,
      useFactory: authServiceFactory,
      deps: [
        CognitoAuthService,
        KeycloakAuthService,
      ],
    },

    provideOAuthClient(),

    UrlHelperService,

    {
      provide: OAuthLogger,
      useValue: console,
    },

    {
      provide: APP_INITIALIZER,
      useFactory: authInit,
      deps: [SiteminderAuthService],
      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorServices,
      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true,
    },

    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],

  bootstrap: [
    AppComponent,
  ],

  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
  ],
})
export class AppModule {}